/* ==========================================================================
   kbesne.js
   Animation runtime for the Kbesne theme sections.

   Design rules this file enforces:
   1. Nothing is hidden by CSS. If GSAP fails to load, every section still
      renders its content — animation is an enhancement, never a gate.
   2. Every module is mounted against a scope element and returns a teardown.
      The theme editor destroys and re-injects section DOM on every edit;
      without teardown you leak ScrollTriggers and the customizer locks up
      after a handful of changes.
   3. Reveals reverse on scroll-up rather than firing once.
   4. prefers-reduced-motion kills scrubbed and looping motion. Motion that
      answers a click or a tap still runs, shortened.
   ========================================================================== */

(function () {
  'use strict';

  if (window.Kbesne) return;

  var Kbesne = window.Kbesne = {
    modules: {},
    reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    hover: window.matchMedia('(hover: hover) and (pointer: fine)').matches
  };

  /* ---------------------------------------------------------------
     gsap readiness
     --------------------------------------------------------------- */
  var queue = [];
  var gsapReady = false;

  function whenReady(fn) {
    if (gsapReady) { fn(); return; }
    queue.push(fn);
  }

  function checkGsap(tries) {
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      gsapReady = true;
      document.documentElement.classList.add('kb-anim-ready');
      queue.splice(0).forEach(function (fn) { fn(); });
      return;
    }
    if (tries > 40) return;               // ~4s, then give up silently
    setTimeout(function () { checkGsap(tries + 1); }, 100);
  }
  checkGsap(0);

  /* ---------------------------------------------------------------
     helpers
     --------------------------------------------------------------- */
  function qa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  Kbesne.register = function (name, factory) { Kbesne.modules[name] = factory; };

  /* NFC ripple burst — used by the hero card and the tap sequence */
  Kbesne.ripples = function (container, spread) {
    if (!container || !window.gsap) return null;
    var rings = qa(container, '.kb-ripple');
    var tl = window.gsap.timeline();
    rings.forEach(function (r, i) {
      tl.fromTo(r,
        { scale: 0.25, opacity: 0.55 },
        { scale: spread || 3.4, opacity: 0, duration: 1.5, ease: 'power2.out' },
        i * 0.22);
    });
    return tl;
  };

  /* 3D tilt that follows the pointer. Returns a teardown. */
  Kbesne.tilt = function (card, opts) {
    if (!card || !window.gsap || Kbesne.reduced || !Kbesne.hover) return function () {};
    var o = opts || {};
    var stage = card.parentElement;
    var maxX = o.maxX || 14, maxY = o.maxY || 16;
    var rx = window.gsap.quickTo(card, 'rotationX', { duration: 0.7, ease: 'power3' });
    var ry = window.gsap.quickTo(card, 'rotationY', { duration: 0.7, ease: 'power3' });

    function move(e) {
      var b = card.getBoundingClientRect();
      rx(window.gsap.utils.clamp(-maxX, maxX, -((e.clientY - (b.top + b.height / 2)) / b.height) * maxX * 1.9));
      ry(window.gsap.utils.clamp(-maxY, maxY, ((e.clientX - (b.left + b.width / 2)) / b.width) * maxY * 1.9));
    }
    function leave() { rx(0); ry(0); }

    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerleave', leave);
    return function () {
      stage.removeEventListener('pointermove', move);
      stage.removeEventListener('pointerleave', leave);
      window.gsap.killTweensOf(card);
    };
  };

  /* Scroll reveals. Reverse on the way back up. */
  Kbesne.reveals = function (root) {
    if (!window.gsap) return;
    var items = qa(root, '.kb-rise');
    if (!items.length) return;

    if (Kbesne.reduced) { window.gsap.set(items, { opacity: 1, y: 0 }); return; }

    items.forEach(function (el) {
      window.gsap.set(el, { opacity: 0, y: 22 });
      window.gsap.to(el, {
        opacity: 1, y: 0, duration: 0.75, ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          toggleActions: 'play none none reverse'
        }
      });
    });
  };

  /* ---------------------------------------------------------------
     mount / unmount
     --------------------------------------------------------------- */
  function mountOne(el) {
    if (el.__kbMounted) return;
    var name = el.getAttribute('data-kb-module');
    var factory = Kbesne.modules[name];
    if (!factory) return;
    if (el.getAttribute('data-kb-anim') === 'false') {
      el.__kbMounted = true;                 // merchant turned animation off
      return;
    }
    el.__kbMounted = true;
    el.__kbTeardown = factory(el) || function () {};
  }

  Kbesne.mount = function (scope) {
    var root = scope || document;
    whenReady(function () {
      var els = [];
      if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute('data-kb-module')) els.push(root);
      els = els.concat(qa(root, '[data-kb-module]'));
      els.forEach(mountOne);
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    });
  };

  Kbesne.unmount = function (scope) {
    var root = scope || document;
    var els = qa(root, '[data-kb-module]');
    if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute('data-kb-module')) els.push(root);

    els.forEach(function (el) {
      if (typeof el.__kbTeardown === 'function') {
        try { el.__kbTeardown(); } catch (err) { /* keep tearing down */ }
      }
      el.__kbTeardown = null;
      el.__kbMounted = false;
    });

    /* kill any trigger whose element lives inside the removed DOM */
    if (window.ScrollTrigger) {
      window.ScrollTrigger.getAll().forEach(function (st) {
        var t = st.trigger || st.vars.trigger;
        if (t && root.contains && root.contains(t)) st.kill();
      });
    }
  };

  /* ---------------------------------------------------------------
     MODULE: hero
     One orchestrated load sequence, then pointer-reactive tilt.
     --------------------------------------------------------------- */
  Kbesne.register('hero', function (root) {
    var gsap = window.gsap;
    var card = root.querySelector('.kb-card');
    var letters = qa(root, '[data-kb-letter]');
    var cleanups = [];

    if (!Kbesne.reduced) {
      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      if (letters.length) tl.from(letters, { yPercent: 115, duration: 0.9, stagger: 0.035 });
      tl.from(qa(root, '[data-kb-fade]'), { y: 16, opacity: 0, duration: 0.7, stagger: 0.08 }, '-=0.5');
      if (card) {
        tl.from(card, { y: 70, opacity: 0, rotateX: 22, scale: 0.94, duration: 1.1 }, '-=0.7')
          .add(function () { Kbesne.ripples(root.querySelector('.kb-ripples'), 3.4); }, '-=0.35');
      }
      cleanups.push(function () { tl.kill(); });
    }

    if (card) {
      cleanups.push(Kbesne.tilt(card));
      var tap = function () { Kbesne.ripples(root.querySelector('.kb-ripples'), 3.4); };
      card.addEventListener('click', tap);
      cleanups.push(function () { card.removeEventListener('click', tap); });
    }

    return function () { cleanups.forEach(function (fn) { fn(); }); };
  });

  /* ---------------------------------------------------------------
     MODULE: sequence
     Pinned, scrubbed: phone travels in, taps, profile fills the screen.
     --------------------------------------------------------------- */
  Kbesne.register('sequence', function (root) {
    var gsap = window.gsap;
    var pin = root.querySelector('[data-kb-pin]');
    var card = root.querySelector('.kb-card');
    var phone = root.querySelector('[data-kb-phone]');
    var screen = root.querySelector('[data-kb-screen]');
    var beats = qa(root, '[data-kb-beat]');

    if (!pin || !beats.length) return;

    /* Reduced motion, or too narrow to pin comfortably: show every beat
       stacked and skip the scrub entirely. */
    if (Kbesne.reduced) {
      root.classList.add('kb-seq--static');
      gsap.set(beats, { opacity: 1, position: 'relative', y: 0 });
      if (screen) gsap.set(screen, { opacity: 1 });
      return;
    }

    gsap.set(phone, { xPercent: 150, yPercent: -10, rotate: 12, opacity: 0 });
    gsap.set(card, { rotationX: 52, rotationZ: -8, y: 40 });
    gsap.set(beats, { opacity: 0, y: 20 });
    gsap.set(beats[0], { opacity: 1, y: 0 });
    if (screen) gsap.set(screen, { opacity: 0 });

    var scrub = parseFloat(root.getAttribute('data-kb-scrub')) || 0.8;
    var len = parseInt(root.getAttribute('data-kb-length'), 10) || 260;

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: pin,
        start: 'top top',
        end: '+=' + len + '%',
        pin: true,
        scrub: scrub,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });

    tl.to(phone, { xPercent: 18, yPercent: -26, rotate: -4, opacity: 1, duration: 1.1, ease: 'power2.out' })
      .to(card, { rotationX: 34, rotationZ: -3, duration: 1.1 }, '<');

    /* advance through however many beats the merchant added */
    for (var i = 1; i < beats.length; i++) {
      tl.to(beats[i - 1], { opacity: 0, y: -20, duration: 0.3 }, i === 1 ? '>-0.25' : '+=0.5')
        .to(beats[i], { opacity: 1, y: 0, duration: 0.35 });

      if (i === 1) {
        tl.add(function () { Kbesne.ripples(root.querySelector('.kb-ripples'), 4.2); }, '<')
          .to(phone, { yPercent: -34, duration: 0.28, ease: 'power2.out' }, '<');
        if (screen) {
          tl.to(screen, { opacity: 1, duration: 0.35 }, '<0.1')
            .fromTo(qa(screen, '[data-kb-screenitem]'),
              { y: 14, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.5, stagger: 0.06 }, '<');
        }
      }
      if (i === 2) {
        var save = screen && screen.querySelector('[data-kb-save]');
        if (save) tl.fromTo(save, { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(2)' }, '<');
        tl.to(card, { rotationX: 44, y: 60, opacity: 0.55, duration: 0.8 }, '<');
      }
    }

    return function () {
      if (tl.scrollTrigger) tl.scrollTrigger.kill(true);
      tl.kill();
      gsap.set([phone, card, screen].filter(Boolean), { clearProps: 'all' });
      gsap.set(beats, { clearProps: 'all' });
    };
  });

  /* ---------------------------------------------------------------
     MODULE: profile
     The "edit once, every card updates" swap.
     --------------------------------------------------------------- */
  Kbesne.register('profile', function (root) {
    var gsap = window.gsap;
    var rows = qa(root, '[data-kb-swapval]');
    var sets = [];
    qa(root, '[data-kb-swapset]').forEach(function (s) {
      sets.push((s.getAttribute('data-kb-swapset') || '').split('|'));
    });

    if (!rows.length || sets.length < 2 || Kbesne.reduced) return;

    var i = 0, call = null, st = null, killed = false;

    function cycle() {
      if (killed) return;
      i = (i + 1) % sets.length;
      var tl = gsap.timeline({
        onComplete: function () { call = gsap.delayedCall(3.2, cycle); }
      });
      rows.forEach(function (el, n) {
        var next = sets[i][n];
        if (typeof next === 'undefined') return;
        tl.to(el, {
          opacity: 0, y: -8, duration: 0.22, ease: 'power2.in',
          onComplete: function () { el.textContent = next; }
        }, n * 0.06)
          .to(el, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, '>');
      });
    }

    st = window.ScrollTrigger.create({
      trigger: root, start: 'top 70%', once: true,
      onEnter: function () { call = gsap.delayedCall(1.4, cycle); }
    });

    return function () {
      killed = true;
      if (call) call.kill();
      if (st) st.kill();
      gsap.killTweensOf(rows);
    };
  });

  /* ---------------------------------------------------------------
     MODULE: buy
     Tilt + live material change when a variant is picked.
     --------------------------------------------------------------- */
  Kbesne.register('buy', function (root) {
    var gsap = window.gsap;
    var card = root.querySelector('.kb-card');
    var face = root.querySelector('.kb-card__face');
    var cleanups = [];

    if (card) cleanups.push(Kbesne.tilt(card, { maxX: 10, maxY: 12 }));

    qa(root, '[data-kb-material]').forEach(function (input) {
      function change() {
        var raw = input.getAttribute('data-kb-material');
        if (!raw || !face) return;
        var c = raw.split('|');
        if (c.length < 3) return;
        gsap.to(face, {
          '--kb-bamboo-lit': c[0].trim(),
          '--kb-bamboo': c[1].trim(),
          '--kb-bamboo-deep': c[2].trim(),
          duration: 0.5, ease: 'power2.out'
        });
        gsap.fromTo(card, { rotationY: -8 }, { rotationY: 0, duration: 0.6, ease: 'power3.out' });
      }
      input.addEventListener('change', change);
      input.addEventListener('click', change);
      cleanups.push(function () {
        input.removeEventListener('change', change);
        input.removeEventListener('click', change);
      });
    });

    return function () { cleanups.forEach(function (fn) { fn(); }); };
  });

  /* ---------------------------------------------------------------
     MODULE: reveal — generic, for sections with no bespoke motion
     --------------------------------------------------------------- */
  Kbesne.register('reveal', function (root) {
    Kbesne.reveals(root);
    return function () { window.gsap.set(qa(root, '.kb-rise'), { clearProps: 'all' }); };
  });

  /* every module also gets the generic reveals inside its own scope */
  var _mountOne = mountOne;
  mountOne = function (el) {
    var already = el.__kbMounted;
    _mountOne(el);
    if (!already && el.getAttribute('data-kb-module') !== 'reveal' &&
        el.getAttribute('data-kb-anim') !== 'false') {
      Kbesne.reveals(el);
    }
  };

  /* ---------------------------------------------------------------
     boot + Shopify theme editor lifecycle
     --------------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Kbesne.mount(document); });
  } else {
    Kbesne.mount(document);
  }

  /* Lazy-loaded images change page height and push every trigger out of
     position. Refresh once everything has actually landed. */
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:load', function (e) {
    Kbesne.unmount(e.target);
    Kbesne.mount(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    Kbesne.unmount(e.target);
  });
  document.addEventListener('shopify:section:select', function () {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:block:select', function () {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
})();
